import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from '@kostbar/i18n'
import { useCreateSection } from '../hooks/use-section-mutations'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useCategories } from '@/features/categories/hooks/use-categories'

const buildAddSectionSchema = (tMenus: (key: string) => string) =>
  z.object({
    name: z
      .string()
      .min(1, tMenus('validation.sectionNameRequired'))
      .max(200, tMenus('validation.sectionNameMax')),
    categoryId: z.number().int().optional(),
    sortOrder: z.number().int(),
    dineInOnly: z.boolean(),
    dailySpecial: z.boolean(),
    menuCard: z.boolean(),
  })

type AddSectionSchema = ReturnType<typeof buildAddSectionSchema>
type AddSectionFormData = z.infer<AddSectionSchema>

interface AddSectionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  menuId: number
}

export function AddSectionDialog({
  open,
  onOpenChange,
  menuId,
}: AddSectionDialogProps) {
  const { t: tMenus } = useTranslation('menus')
  const createSection = useCreateSection()
  const { data: categoriesData } = useCategories()

  const form = useForm<AddSectionFormData>({
    resolver: zodResolver(buildAddSectionSchema(tMenus)),
    defaultValues: {
      name: '',
      sortOrder: 0,
      dineInOnly: false,
      dailySpecial: false,
      // Fail open: a new section publishes to the Speisekarte unless someone unticks it.
      menuCard: true,
    },
  })

  const onSubmit = (data: AddSectionFormData) => {
    createSection.mutate(
      { menuId, data },
      {
        onSuccess: () => {
          form.reset()
          onOpenChange(false)
        },
      },
    )
  }

  const categories = categoriesData?.items || []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{tMenus('section.addTitle')}</DialogTitle>
          <DialogDescription>
            {tMenus('section.addDescription')}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{tMenus('section.name')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={tMenus('section.namePlaceholder')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="categoryId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{tMenus('section.categoryOptional')}</FormLabel>
                  <Select
                    value={field.value?.toString() || 'none'}
                    onValueChange={(value) =>
                      field.onChange(
                        value === 'none' ? undefined : parseInt(value),
                      )
                    }
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={tMenus('section.noCategory')}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">
                        {tMenus('section.noCategory')}
                      </SelectItem>
                      {categories.map((category) => (
                        <SelectItem
                          key={category.id}
                          value={category.id.toString()}
                        >
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    {tMenus('section.categoryHint')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="sortOrder"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{tMenus('section.sortOrder')}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      {...field}
                      onChange={(e) =>
                        field.onChange(parseInt(e.target.value) || 0)
                      }
                    />
                  </FormControl>
                  <FormDescription>
                    {tMenus('section.sortHint')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="menuCard"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>{tMenus('section.menuCard')}</FormLabel>
                    <FormDescription>
                      {tMenus('section.menuCardHint')}
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="dineInOnly"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>{tMenus('section.dineInOnly')}</FormLabel>
                    <FormDescription>
                      {tMenus('section.dineInOnlyHint')}
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="dailySpecial"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>{tMenus('section.dailySpecial')}</FormLabel>
                    <FormDescription>
                      {tMenus('section.dailySpecialHint')}
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={createSection.isPending}
              >
                {tMenus('section.cancel')}
              </Button>
              <Button type="submit" disabled={createSection.isPending}>
                {createSection.isPending
                  ? tMenus('section.adding')
                  : tMenus('section.add')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
